import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { fetchWarrantyAlerts } from '../../../services/Http/Api';

const defaultStats = {
    expiring_soon: 0,
    expired: 0,
    missing_activation: 0
};

const formatDate = (value, fallback) => {
    if (!value) {
        return fallback;
    }

    try {
        return new Date(value).toLocaleDateString();
    } catch (e) {
        return fallback;
    }
};

const WarrantyAlerts = () => {
    const { t } = useTranslation();
    const [filters, setFilters] = useState({
        within_days: 60,
        missing_window: 30,
        limit: 10
    });
    const [stats, setStats] = useState(defaultStats);
    const [lists, setLists] = useState({
        expiring_soon: [],
        expired: [],
        missing_activation: []
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const loadAlerts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchWarrantyAlerts(filters);
            setStats(data.stats || defaultStats);
            setLists({
                expiring_soon: data.expiring_soon || [],
                expired: data.expired || [],
                missing_activation: data.missing_activation || []
            });
        } catch (err) {
            setError(err?.message || 'Unable to load warranty alerts');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        loadAlerts();
    }, [loadAlerts]);

    const sections = useMemo(() => ([
        {
            key: 'expiring_soon',
            title: t('Items.Warranty.sections.expiringSoon.title'),
            description: t('Items.Warranty.sections.expiringSoon.description', { days: filters.within_days }),
            tone: 'warning',
            data: lists.expiring_soon
        },
        {
            key: 'expired',
            title: t('Items.Warranty.sections.expired.title'),
            description: t('Items.Warranty.sections.expired.description'),
            tone: 'danger',
            data: lists.expired
        },
        {
            key: 'missing_activation',
            title: t('Items.Warranty.sections.missingActivation.title'),
            description: t('Items.Warranty.sections.missingActivation.description', { days: filters.missing_window }),
            tone: 'info',
            data: lists.missing_activation
        }
    ]), [lists, filters.within_days, filters.missing_window, t]);

    const handleNumericChange = (field) => (event) => {
        const nextValue = Math.max(1, Number(event.target.value));
        setFilters((prev) => ({ ...prev, [field]: nextValue }));
    };

    const formatDays = (value) => {
        if (value === null || value === undefined) {
            return '—';
        }

        return value >= 0
            ? t('Items.Warranty.labels.daysRemaining', { count: value })
            : t('Items.Warranty.labels.daysOverdue', { count: Math.abs(value) });
    };

    return (
        <Wrapper>
            <Header>
                <div>
                    <Title>{t('Items.Warranty.title')}</Title>
                    <Subtitle>{t('Items.Warranty.subtitle')}</Subtitle>
                </div>
                <Filters>
                    <FilterGroup>
                        <label>{t('Items.Warranty.filters.withinDays')}</label>
                        <input
                            type="number"
                            min={1}
                            value={filters.within_days}
                            onChange={handleNumericChange('within_days')}
                        />
                    </FilterGroup>
                    <FilterGroup>
                        <label>{t('Items.Warranty.filters.missingActivation')}</label>
                        <input
                            type="number"
                            min={1}
                            value={filters.missing_window}
                            onChange={handleNumericChange('missing_window')}
                        />
                    </FilterGroup>
                    <FilterGroup>
                        <label>{t('Items.Warranty.filters.limit')}</label>
                        <input
                            type="number"
                            min={5}
                            value={filters.limit}
                            onChange={handleNumericChange('limit')}
                        />
                    </FilterGroup>
                    <RefreshButton type="button" onClick={loadAlerts}>
                        {t('Items.Warranty.filters.refresh')}
                    </RefreshButton>
                </Filters>
            </Header>

            {error && <ErrorBanner>{error}</ErrorBanner>}

            {loading ? (
                <Centered>
                    <Spinner color="primary" />
                </Centered>
            ) : (
                <>
                    <SummaryGrid>
                        <SummaryCard tone="warning">
                            <label>{t('Items.Warranty.summary.expiringSoon')}</label>
                            <strong>{stats.expiring_soon || 0}</strong>
                        </SummaryCard>
                        <SummaryCard tone="danger">
                            <label>{t('Items.Warranty.summary.expired')}</label>
                            <strong>{stats.expired || 0}</strong>
                        </SummaryCard>
                        <SummaryCard tone="info">
                            <label>{t('Items.Warranty.summary.missingActivation')}</label>
                            <strong>{stats.missing_activation || 0}</strong>
                        </SummaryCard>
                    </SummaryGrid>

                    <SectionsGrid>
                        {sections.map((section) => (
                            <AlertCard key={section.key} tone={section.tone}>
                                <CardHeader>
                                    <h5>{section.title}</h5>
                                    <small>{section.description}</small>
                                </CardHeader>
                                {section.data.length === 0 ? (
                                    <EmptyState>{t('Items.Warranty.empty')}</EmptyState>
                                ) : (
                                    <AlertTable>
                                        <thead>
                                            <tr>
                                                <th>{t('Items.Warranty.columns.item')}</th>
                                                <th>{t('Items.Warranty.columns.buyer')}</th>
                                                <th>{t('Items.Warranty.columns.venue')}</th>
                                                <th>{t('Items.Warranty.columns.saleDate')}</th>
                                                <th>{t('Items.Warranty.columns.expiry')}</th>
                                                <th>{t('Items.Warranty.columns.days')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {section.data.map((row) => (
                                                <tr key={`${section.key}-${row.item_id}`}>
                                                    <td>
                                                        <strong>{row.product_name}</strong>
                                                        <small>{row.serial_number}</small>
                                                    </td>
                                                    <td>{row.buyer_name || t('common.none')}</td>
                                                    <td>{row.venue_name || t('common.none')}</td>
                                                    <td>{formatDate(row.sale_date, '—')}</td>
                                                    <td>{formatDate(row.warranty_end_date, '—')}</td>
                                                    <td>{formatDays(row.days_remaining)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </AlertTable>
                                )}
                            </AlertCard>
                        ))}
                    </SectionsGrid>
                </>
            )}
        </Wrapper>
    );
};

export default WarrantyAlerts;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 0.75rem;
`;

const Title = styled.h3`
    margin: 0;
    color: rgba(130, 22, 146, 1);
`;

const Subtitle = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const Filters = styled.div`
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    align-items: flex-end;
`;

const FilterGroup = styled.label`
    display: flex;
    flex-direction: column;
    font-size: 0.875rem;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};

    input {
        margin-top: 0.25rem;
        padding: 0.5rem 0.625rem;
        border-radius: 6px;
        border: 1px solid rgba(0, 0, 0, 0.1);
        width: 90px;
    }
`;

const RefreshButton = styled.button`
    padding: 0.625rem 1rem;
    border: none;
    border-radius: 8px;
    background: rgba(130, 22, 146, 1);
    color: #fff;
    font-weight: 600;
    cursor: pointer;
`;

const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
`;

const SummaryCard = styled.div`
    border: 1px solid ${({ tone }) => (tone === 'danger' ? 'rgba(220, 53, 69, 0.3)' : tone === 'warning' ? 'rgba(255, 193, 7, 0.4)' : 'rgba(20, 123, 255, 0.3)')};
    background: ${({ tone }) => (tone === 'danger' ? 'rgba(220, 53, 69, 0.08)' : tone === 'warning' ? 'rgba(255, 193, 7, 0.15)' : 'rgba(20, 123, 255, 0.08)')};
    border-radius: 10px;
    padding: 1rem;

    label {
        display: block;
        font-size: 0.875rem;
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
    }

    strong {
        display: block;
        font-size: 1.6rem;
        margin-top: 0.25rem;
        color: rgba(26, 26, 26, 0.9);
    }
`;

const SectionsGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const AlertCard = styled.div`
    border: 1px solid ${({ tone }) => (tone === 'danger' ? 'rgba(220, 53, 69, 0.3)' : tone === 'warning' ? 'rgba(255, 193, 7, 0.4)' : 'rgba(20, 123, 255, 0.3)')};
    border-radius: 12px;
    background: #fff;
    padding: 1rem;
`;

const CardHeader = styled.div`
    margin-bottom: 0.75rem;

    h5 {
        margin: 0;
        color: rgba(130, 22, 146, 1);
    }

    small {
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
    }
`;

const AlertTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th, td {
        padding: 0.5rem 0.375rem;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        text-align: left;
    }

    th {
        text-transform: uppercase;
        font-size: 0.75rem;
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
    }

    td small {
        display: block;
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
    }
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 1.25rem 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const ErrorBanner = styled.div`
    background: rgba(220, 53, 69, 0.1);
    color: #721c24;
    border: 1px solid rgba(220, 53, 69, 0.3);
    padding: 0.625rem 1rem;
    border-radius: 8px;
`;

const Centered = styled.div`
    display: flex;
    justify-content: center;
    padding: 2.5rem 0;
`;
